import { LocationStatuses } from "src/modules/3rd-party-management/pages/admin/suppliers/details/tabs-content/kyb/components/company-details/drivers/dnb-enterprise/constants";
import {
    buildReportHeaderSection,
    formatPdfDate,
    formatPdfDisplay,
    formatPdfMoney,
    formatPdfYesNoUnknown,
    keyValueSection,
    recordsSection,
} from "./providerPdfShared";

export const parseDnbEnterpriseOrganizationData = (company) => {
    if (!company) {
        return null;
    }

    const raw = company.organizationData ?? company.organization_data;
    if (!raw) {
        return null;
    }

    try {
        const data = typeof raw === "string" ? JSON.parse(raw) : raw;
        return data && typeof data === "object" ? data : null;
    } catch (e) {
        return null;
    }
};

const formatNotProvided = (value) => {
    if (value === null || value === undefined || value === "") {
        return "Not provided";
    }
    return String(value);
};

const formatOrNa = (value) => {
    if (value === null || value === undefined || value === "") {
        return "N/A";
    }
    return String(value);
};

const formatOrDash = (value) => {
    if (value === null || value === undefined || value === "") {
        return "—";
    }
    return String(value);
};

const formatMoneyOrDash = (value, currency = "EUR") => {
    if (value === null || value === undefined || value === "") {
        return "—";
    }
    return formatPdfMoney(value, currency);
};

const formatBusinessNumber = (quickcheck = {}) => {
    if (!quickcheck.business_number) {
        return "Not provided";
    }
    return [
        quickcheck.business_number,
        quickcheck.business_number_description
            ? `(${quickcheck.business_number_description})`
            : null,
    ].filter(Boolean).join(" ");
};

const formatAddress = (address) => {
    if (!address) {
        return "Not provided";
    }
    if (Array.isArray(address)) {
        const joined = address.filter(Boolean).join(", ");
        return joined || "Not provided";
    }
    return String(address);
};

const formatPhone = (quickcheck = {}) => {
    const phone = [quickcheck.country_calling_code, quickcheck.phone_number]
        .filter(Boolean)
        .join(" ");
    return phone || "Not provided";
};

const formatLocationStatus = (locationStatus) => {
    if (!locationStatus) {
        return "Not provided";
    }
    const title = LocationStatuses[locationStatus]?.title;
    return title ? `${locationStatus} (${title})` : String(locationStatus);
};

const formatEntityLine = (entity) => {
    if (!entity) {
        return null;
    }
    return `${entity.trade_name || entity.name || "-"} (${entity.duns || "-"}, ${entity.country || "-"})`;
};

const formatScorePayload = (value) => {
    if (value && value.length) {
        try {
            return JSON.stringify(value);
        } catch (e) {
            return String(value);
        }
    }
    return "No Data";
};

const formatDateOrNa = (value) => {
    if (!value) {
        return "N/A";
    }
    return formatPdfDate(value);
};

/**
 * Build PDF sections matching the Dun & Bradstreet Enterprise KYB detail page.
 * Omits corporate-structure graph (visual-only).
 */
export const buildDnbEnterpriseExportSections = (organizationData, header) => {
    if (!organizationData || typeof organizationData !== "object") {
        return [];
    }

    const {
        quickcheck = {},
        location_status,
        parent,
        domestic_ultimate,
        global_ultimate,
        is_subsidiary_location,
        main_branch_indication,
        previous_sales,
        net_income,
        total_assets,
        liquid_assets,
        inventory,
        total_liabilities,
        accounts_payable,
        accounts_receivable,
        total_current_assets,
        total_current_liabilities,
        previous_net_worth,
        accounts_audited,
        statement_consolidated,
        previous_statement_date,
        figures_estimated,
        year_period,
        current_ratio,
        quick_ratio,
        paydex_score_earlier,
        high_credit,
        average_high_credit,
        total_payments,
        credit_score,
        global_failure_score,
        failure_score = [],
        claims_indicator,
        export_indicator,
        indate_indicator,
    } = organizationData;

    const currency = quickcheck.currency || "EUR";
    const sections = [];

    const headerSection = buildReportHeaderSection(header);
    if (headerSection) {
        sections.push(headerSection);
    }

    sections.push(keyValueSection("Company Information", [
        { title: "Trade Name", details: formatNotProvided(quickcheck.trade_name) },
        { title: "DUNS", details: formatNotProvided(quickcheck.duns) },
        { title: "D&B Key", details: formatNotProvided(quickcheck.dnb_key) },
        {
            title: "Legal Form Description",
            details: formatNotProvided(quickcheck.legal_form_description),
        },
        { title: "Business Number", details: formatBusinessNumber(quickcheck) },
        {
            title: "Business Number Type",
            details: formatNotProvided(quickcheck.business_number_type),
        },
        { title: "Incorporation Year", details: formatOrNa(quickcheck.incorporation_year) },
        { title: "Start Year", details: formatOrNa(quickcheck.start_year) },
        { title: "CEO Name", details: formatNotProvided(quickcheck.ceo_name) },
        { title: "CEO Title", details: formatNotProvided(quickcheck.ceo_title) },
        { title: "Address", details: formatAddress(quickcheck.address) },
        { title: "City", details: formatNotProvided(quickcheck.city) },
        { title: "Sub-Region", details: formatNotProvided(quickcheck.subregion) },
        { title: "Region", details: formatNotProvided(quickcheck.region) },
        { title: "Country", details: formatNotProvided(quickcheck.country) },
        { title: "Postal Code", details: formatNotProvided(quickcheck.postcode) },
        { title: "Phone", details: formatPhone(quickcheck) },
        { title: "Location Status", details: formatLocationStatus(location_status) },
        {
            title: "SIC Activity Code",
            details: formatNotProvided(quickcheck.sic_activity_code),
        },
        {
            title: "SIC Activity Description",
            details: formatNotProvided(quickcheck.sic_activity_description),
        },
        { title: "SIC Version", details: formatNotProvided(quickcheck.sic_version) },
        {
            title: "Local Activity Code",
            details: formatNotProvided(quickcheck.local_activity_code),
        },
        {
            title: "Total Employees",
            details: formatOrDash(quickcheck.employees_total?.employees),
        },
        {
            title: "Employees Estimated",
            details: formatPdfYesNoUnknown(quickcheck.employees_total?.estimated),
        },
        {
            title: "Subsidiaries Included",
            details: formatPdfYesNoUnknown(quickcheck.employees_total?.subsidiaries_included),
        },
        {
            title: "Out Of Business",
            details: formatPdfYesNoUnknown(quickcheck.out_of_business),
        },
    ]));

    const shareholderRows = [];
    if (parent) {
        shareholderRows.push({ title: "Parent", details: formatEntityLine(parent) });
    }
    if (domestic_ultimate) {
        shareholderRows.push({
            title: "Domestic Ultimate",
            details: formatEntityLine(domestic_ultimate),
        });
    }
    if (global_ultimate) {
        shareholderRows.push({
            title: "Global Ultimate",
            details: formatEntityLine(global_ultimate),
        });
    }
    shareholderRows.push(
        {
            title: "Subsidiary Status",
            details: formatPdfYesNoUnknown(is_subsidiary_location),
        },
        {
            title: "Main Branch",
            details: formatPdfYesNoUnknown(main_branch_indication),
        }
    );
    sections.push(keyValueSection("Shareholders", shareholderRows));

    sections.push(keyValueSection("Financial Summary", [
        {
            title: "Revenue (Current)",
            details: formatMoneyOrDash(quickcheck.sales, currency),
        },
        {
            title: "Revenue (Previous)",
            details: formatMoneyOrDash(previous_sales, currency),
        },
        { title: "Net Income", details: formatMoneyOrDash(net_income, currency) },
        {
            title: "Net Worth",
            details: formatMoneyOrDash(quickcheck.net_worth, currency),
        },
        { title: "Total Assets", details: formatMoneyOrDash(total_assets, currency) },
        { title: "Liquid Assets", details: formatMoneyOrDash(liquid_assets, currency) },
        { title: "Inventory", details: formatMoneyOrDash(inventory, currency) },
        {
            title: "Intangible Assets",
            details: formatMoneyOrDash(quickcheck.intangible_assets, currency),
        },
        {
            title: "Total Liabilities",
            details: formatMoneyOrDash(total_liabilities, currency),
        },
        {
            title: "Accounts Payable",
            details: formatMoneyOrDash(accounts_payable, currency),
        },
        {
            title: "Accounts Receivable",
            details: formatMoneyOrDash(accounts_receivable, currency),
        },
        {
            title: "Total Current Assets",
            details: formatMoneyOrDash(total_current_assets, currency),
        },
        {
            title: "Total Current Liabilities",
            details: formatMoneyOrDash(total_current_liabilities, currency),
        },
        {
            title: "Previous Net Worth",
            details: formatMoneyOrDash(previous_net_worth, currency),
        },
        {
            title: "Accounts Audited",
            details: formatPdfYesNoUnknown(accounts_audited),
        },
        {
            title: "Statement Consolidated",
            details: formatPdfYesNoUnknown(statement_consolidated),
        },
        {
            title: "Income Statement Date",
            details: formatDateOrNa(quickcheck.income_statement_date),
        },
        {
            title: "Income Statement End Date",
            details: formatDateOrNa(quickcheck.income_statement_date_end),
        },
        {
            title: "Financial Statement Date",
            details: formatDateOrNa(quickcheck.financial_statement_date),
        },
        {
            title: "Previous Statement Date",
            details: formatDateOrNa(previous_statement_date),
        },
        {
            title: "Figures Estimated",
            details: formatPdfYesNoUnknown(figures_estimated),
        },
        {
            title: "Annual Period",
            details: formatPdfYesNoUnknown(year_period),
        },
    ]));

    sections.push(keyValueSection("Financial Ratios", [
        { title: "Current Ratio", details: formatOrDash(current_ratio) },
        { title: "Quick Ratio", details: formatOrDash(quick_ratio) },
    ]));

    sections.push(keyValueSection("Payment & Credit", [
        {
            title: "PAYDEX Score",
            details: formatOrDash(quickcheck.paydex_score),
        },
        {
            title: "Previous PAYDEX",
            details: formatOrDash(paydex_score_earlier),
        },
        {
            title: "High Credit",
            details: formatMoneyOrDash(high_credit, currency),
        },
        {
            title: "Average High Credit",
            details: formatMoneyOrDash(average_high_credit, currency),
        },
        {
            title: "Total Payments Recorded",
            details: formatPdfDisplay(total_payments),
        },
        {
            title: "Maximum Credit",
            details: formatMoneyOrDash(quickcheck.maximum_credit, currency),
        },
        { title: "Credit Score", details: formatScorePayload(credit_score) },
        {
            title: "Global Failure Score",
            details: formatScorePayload(global_failure_score),
        },
    ]));

    if (Array.isArray(failure_score) && failure_score.length > 0) {
        sections.push(recordsSection(
            "Risk & Failure Score",
            [
                { key: "scoreDate", label: "Score Date", width: "16%" },
                { key: "algorithmId", label: "Algorithm ID", width: "16%" },
                { key: "score", label: "Score", width: "12%" },
                { key: "percentile", label: "National Percentile", width: "16%" },
                { key: "commentaries", label: "Commentaries", width: "40%" },
            ],
            failure_score.map((fs, index) => ({
                id: `failure-score-${index}`,
                scoreDate: formatPdfDate(fs?.score_date),
                algorithmId: formatPdfDisplay(fs?.algorithm_id),
                score: formatPdfDisplay(fs?.score),
                percentile: formatPdfDisplay(fs?.national_percentile),
                commentaries: Array.isArray(fs?.score_commentaries)
                    ? fs.score_commentaries
                        .map((c) => c?.description || c?.code)
                        .filter(Boolean)
                        .join("; ") || "-"
                    : "-",
            }))
        ));
    }

    sections.push(keyValueSection("Legal & Operational Events", [
        { title: "Disaster", details: formatPdfYesNoUnknown(quickcheck.disaster) },
        {
            title: "Financial Embarrassment",
            details: formatPdfYesNoUnknown(quickcheck.financial_embarrassment),
        },
        {
            title: "Financial Legal Events",
            details: formatPdfYesNoUnknown(quickcheck.financial_legal_events),
        },
        {
            title: "Suit / Judgement",
            details: formatPdfYesNoUnknown(quickcheck.suit_judgement),
        },
        {
            title: "Criminal Proceedings",
            details: formatPdfYesNoUnknown(quickcheck.criminal_proceedings),
        },
        {
            title: "Other Events",
            details: formatPdfYesNoUnknown(quickcheck.other_events),
        },
        {
            title: "Operational Events",
            details: formatPdfYesNoUnknown(quickcheck.operational_events),
        },
        {
            title: "Claims Indicator",
            details: formatPdfYesNoUnknown(claims_indicator),
        },
        {
            title: "Secured Filings",
            details: formatPdfYesNoUnknown(quickcheck.secured_filings),
        },
        {
            title: "Export Indicator",
            details: formatPdfYesNoUnknown(export_indicator),
        },
        {
            title: "Indate Indicator",
            details: formatPdfYesNoUnknown(indate_indicator),
        },
    ]));

    return sections.filter(Boolean);
};
